import { zodResolver } from "@hookform/resolvers/zod";
import {
    Modal,
    RichTextField,
} from "@twinkltech/curriculum-architect-component-library";
import { useTwinklAnalytics } from "@twinkltech/web-analytics-ts";
import { Button, Text } from "@twinkltech/web-ui";
import { useId, useMemo, useRef, useState } from "react";
import { type SubmitHandler, useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useScrollPosition } from "@/pages/CurriculumList/hooks/useScrollPosition";

import type {
    BasicResourceCell,
    TableResource,
} from "@/states/unitTableReducer/unitTableReducer";

import { COMMON_NS } from "@/i18n/common/common.i18n";
import { useFeedback } from "@/providers/Feedback/Feedback.context";

import { MANAGE_ADAPTATION_NOTES_MODAL_NS } from "./ManageAdaptationNotesModal.i18n";

const SchemeAdaptationNotesFormSchema = z.object({
  adaptationNotes: z.string().max(500).optional(),
});

type SchemeAdaptationNotesFormData = z.infer<
  typeof SchemeAdaptationNotesFormSchema
>;

export const ManageAdaptationNotesModal = ({
  cell,
  cohort,
  onClose,
  onSubmit,
  open,
  scheme,
  termEnd,
  termStart,
}: {
  open: boolean;
  cell: BasicResourceCell;
  scheme: TableResource;
  onClose: () => void;
  onSubmit: (data: { cellId: string; adaptationNote: string }) => void;
  cohort: string;
  termStart: string;
  termEnd: string;
}) => {
  const { t } = useTranslation(MANAGE_ADAPTATION_NOTES_MODAL_NS);
  const { incrementEngagementCount } = useFeedback();

  const { click } = useTwinklAnalytics();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const modalBodyRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const { isScrollPresent } = useScrollPosition({
    initialState: false,
    ref: modalBodyRef,
  });

  const formId = useId();

  const formDefaultValues = useMemo<SchemeAdaptationNotesFormData>(
    () => ({
      adaptationNotes: cell.adaptationNote || "",
    }),
    [cell.adaptationNote]
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<
    SchemeAdaptationNotesFormData,
    unknown,
    SchemeAdaptationNotesFormData
  >({
    resolver: zodResolver<
      SchemeAdaptationNotesFormData,
      unknown,
      SchemeAdaptationNotesFormData
    >(SchemeAdaptationNotesFormSchema),
    defaultValues: formDefaultValues,
  });

  const { field: adaptationNotesField } = useController({
    control,
    name: "adaptationNotes",
  });

  const onFormSubmit: SubmitHandler<SchemeAdaptationNotesFormData> = async (
    data,
    event
  ) => {
    event?.preventDefault();

    if (isSubmitting) {
      return;
    }

    incrementEngagementCount();

    setIsSubmitting(true);

    onSubmit({
      cellId: cell.id,
      adaptationNote: data.adaptationNotes || "",
    });

    click("click_cta", {
      ctaCopy: "Save Changes",
      clickLocation: "Manage Adaptation Notes Modal",
    });

    onClose();

    setIsSubmitting(false);
  };

  return (
    <Modal
      ref={modalRef}
      className=" overflow-hidden"
      open={open}
      width="lg"
      onClose={onClose}
    >
      <Modal.Header>
        <Text as="h1" variant="2xl-medium">
          {scheme.name}
        </Text>
      </Modal.Header>
      <Modal.Body ref={modalBodyRef} className="overflow-y-auto max-h-[70vh]">
        <form
          className="py-2 gap-4 flex flex-col"
          id={formId}
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <RichTextField
            errorText={errors.adaptationNotes?.message}
            infoText="500 character limit"
            label={"Adaptation Notes"}
            markdown={adaptationNotesField.value || ""}
            onBlur={adaptationNotesField.onBlur}
            onChange={adaptationNotesField.onChange}
          />
          <div className="flex flex-col gap-6 mt-1">
            <div aria-labelledby="cohort-heading" role="group">
              <Text as="h2" id="cohort-heading" variant="lg-medium">
                {t("terms.cohort", { ns: COMMON_NS })}
              </Text>
              <Text variant="base-normal">{cohort}</Text>
            </div>

            <div aria-labelledby="term-start-heading" role="group">
              <Text as="h2" id="term-start-heading" variant="lg-medium">
                {t("start_term_label")}
              </Text>
              <Text variant="base-normal">{termStart}</Text>
            </div>

            <div aria-labelledby="term-end-heading" role="group">
              <Text as="h2" id="term-end-heading" variant="lg-medium">
                {t("end_term_label")}
              </Text>
              <Text variant="base-normal">{termEnd}</Text>
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer
        className={[
          "flex gap-2",
          isScrollPresent ? "border-t border-t-neutral-150" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
        <Button
          disabled={isSubmitting}
          variant="primary"
          onClick={handleSubmit(onFormSubmit)}
        >
          {t("actions.apply-changes", { ns: COMMON_NS })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
